import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Lottie from "lottie-react";
import { Icon } from "@iconify/react";
import doctor from "../../Assets/animation/heroDoctor.json";
import Loading from "../../Shared/Loading";

const Hero = () => {
  const [heroData, setHeroData] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetch(`https://servercareseebd.careseebd.com/api/v1/hero/getHero`)
      .then((r) => r.json())
      .then((d) => { if (d?.data?.length) setHeroData(d.data[0]); })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loading />;

  return (
    <section className="relative min-h-[calc(100vh-64px)] bg-white overflow-hidden flex items-center">
      {/* Soft background decoration */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_-10%,rgba(99,102,241,0.07),transparent)] pointer-events-none" />
      <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full bg-sky-50/60 blur-[100px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-indigo-50/50 blur-[80px] pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-16 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">

        {/* Left — copy */}
        <div className="order-2 lg:order-1">
          {/* Eyebrow */}
          <div className="inline-flex items-center gap-2 bg-indigo-50 border border-indigo-100 rounded-full px-4 py-1.5 mb-8">
            <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 animate-pulse" />
            <span className="text-xs font-semibold text-indigo-700 uppercase tracking-widest">
              Trusted Healthcare Platform
            </span>
          </div>

          {/* Headline */}
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-5xl font-extrabold text-gray-900 ">
            {heroData?.title ? (
              <>
                {heroData.title.split(" ").slice(0, -2).join(" ")}{" "}
                <span className="text-gradient">
                  {heroData.title.split(" ").slice(-2).join(" ")}
                </span>
              </>
            ) : (
              <>
                Your Health,{" "}
                <span className="text-gradient">Our Priority</span>
              </>
            )}
          </h1>

          {/* Body */}
          <div
            className="mt-6 text-base text-gray-500 max-w-xl font-normal space-y-2"
            dangerouslySetInnerHTML={{ __html: heroData?.content || "Expert medical care, personalised for you." }}
          />

          {/* CTAs */}
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link
              to="/contactUs"
              className="group inline-flex items-center gap-2 px-7 py-3.5 bg-brand-gradient text-white text-sm font-semibold rounded-2xl shadow-lg shadow-indigo-500/25 hover:shadow-indigo-500/40 hover:-translate-y-0.5 transition-all duration-200"
            >
              Get Started
              <Icon icon="solar:arrow-right-bold" className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </Link>
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2 px-7 py-3.5 bg-white text-gray-700 text-sm font-semibold rounded-2xl border border-gray-200 hover:border-indigo-200 hover:bg-indigo-50/30 hover:-translate-y-0.5 transition-all duration-200 shadow-sm"
            >
              View Plans
            </Link>
          </div>

          {/* Trust badges */}
          <div className="mt-10 pt-8 border-t border-gray-100 flex flex-wrap items-center gap-6">
            {[
              { icon: "solar:shield-check-bold-duotone", text: "HIPAA Compliant" },
              { icon: "solar:star-bold-duotone", text: "Top Rated 2024" },
              { icon: "solar:lock-bold-duotone", text: "End-to-End Secure" },
            ].map((b) => (
              <span key={b.text} className="flex items-center gap-2 text-sm text-gray-500 font-medium">
                <Icon icon={b.icon} className="w-5 h-5 text-indigo-500" />
                {b.text}
              </span>
            ))}
          </div>
        </div>

        {/* Right — Lottie */}
        <div className="order-1 lg:order-2 relative flex items-center justify-center">
          {/* Decorative rings */}
          <div className="absolute w-[440px] h-[440px] rounded-full border border-indigo-100/60" />
          <div className="absolute w-[340px] h-[340px] rounded-full border border-indigo-200/40" />
          <div className="absolute w-[500px] h-[500px] rounded-full bg-gradient-to-br from-indigo-50/40 to-sky-50/30 blur-2xl" />
          <div className="relative z-10 w-full max-w-[440px]">
            <Lottie animationData={doctor} />
          </div>

          {/* Floating stat cards */}
          <div className="absolute top-8 -left-4 glass-card rounded-2xl px-4 py-3 shadow-xl flex items-center gap-3">
            <div className="w-9 h-9 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <Icon icon="solar:user-heart-bold-duotone" className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-xs font-bold text-gray-900 font-heading">10,000+</p>
              <p className="text-[10px] text-gray-500">Happy Patients</p>
            </div>
          </div>

          <div className="absolute bottom-12 -right-4 glass-card rounded-2xl px-4 py-3 shadow-xl flex items-center gap-3">
            <div className="w-9 h-9 bg-indigo-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <Icon icon="solar:medical-kit-bold-duotone" className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <p className="text-xs font-bold text-gray-900 font-heading">500+</p>
              <p className="text-[10px] text-gray-500">Expert Doctors</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
